import React, { useState, useCallback, useEffect } from 'react';
import './App.css';
import { generate as generateOffline } from './utils/sudokuGenerator';

const API_URL = '/api/sudoku';

function App() {
  const [board, setBoard] = useState([]);
  const [solution, setSolution] = useState([]);
  const [initialBoard, setInitialBoard] = useState([]);
  const [selectedCell, setSelectedCell] = useState(null);
  const [history, setHistory] = useState([]);
  const [gameWon, setGameWon] = useState(false);
  const [difficulty, setDifficulty] = useState('easy');
  const [hintMessage, setHintMessage] = useState('');
  const [hintCell, setHintCell] = useState(null);
  const [mistakes, setMistakes] = useState(0);
  const [errorCells, setErrorCells] = useState([]); // Track cells with invalid numbers
  const [showMenu, setShowMenu] = useState(false); // Mobile menu dropdown
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Generate a new puzzle (works offline)
  const generatePuzzle = useCallback(async (diff = difficulty) => {
    // Try online API first if we're online
    if (isOnline) {
      try {
        const response = await fetch(`${API_URL}/generate?difficulty=${diff}`);
        const data = await response.json();
        
        if (data.success) {
          setBoard(data.puzzle.map(row => [...row]));
          setSolution(data.solution);
          setInitialBoard(data.initialBoard.map(row => [...row]));
          setHistory([]);
          setGameWon(false);
          setHintMessage('');
          setHintCell(null);
          setMistakes(0);
          setErrorCells([]);
          setDifficulty(diff);
          setHintMessage(`New ${diff} puzzle! Let's solve it together! 🌟`);
          return;
        }
      } catch (error) {
        console.log('API failed, falling back to offline generation:', error);
        // Fall through to offline generation
      }
    }

    // Offline generation fallback
    try {
      const puzzle = generateOffline(diff);
      setBoard(puzzle.board.map(row => [...row]));
      setSolution(puzzle.solution);
      setInitialBoard(puzzle.initialBoard.map(row => [...row]));
      setHistory([]);
      setGameWon(false);
      setHintMessage('');
      setHintCell(null);
      setMistakes(0);
      setErrorCells([]);
      setDifficulty(diff);
      setHintMessage(`📴 Offline mode: New ${diff} puzzle generated locally!`);
    } catch (error) {
      console.error('Error generating puzzle:', error);
      setHintMessage('Oops! Something went wrong. Try again!');
    }
  }, [difficulty, isOnline]);

  // Initial puzzle generation
  React.useEffect(() => {
    generatePuzzle('easy');
  }, []);

  // Listen for online/offline events
  React.useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setHintMessage('🌐 Back online! You can use the API features again!');
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      setHintMessage('📴 You\'re offline - continuing with offline mode!');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Handle cell click
  const handleCellClick = (row, col) => {
    if (gameWon) return;
    
    // Clear previous hints
    setHintCell(null);
    setSelectedCell({ row, col });
  };

  // Handle reset - clear all user entries
  const handleReset = () => {
    if (gameWon) return;
    setBoard(initialBoard.map(row => [...row]));
    setHistory([]);
    setMistakes(0);
    setErrorCells([]);
    setHintMessage('Game reset! Starting over! 🔄');
  };

  // Handle mobile number input from input field
  const handleMobileInput = (e) => {
    const value = e.target.value;
    if (value && value >= '1' && value <= '6') {
      handleNumberInput(parseInt(value, 10));
      e.target.value = ''; // Clear the input after processing
    }
    // Don't clear on empty - let onBlur handle invalid input
  };

  // Handle mobile input keypress
  const handleMobileKeyPress = (e) => {
    const value = e.target.value;
    // Allow only 1-6
    if (value && (value < '1' || value > '6')) {
      e.preventDefault();
      e.target.value = '';
    }
  };

  // Handle number input
  const handleNumberInput = async (num) => {
    if (!selectedCell || gameWon) return;
    
    const { row, col } = selectedCell;
    
    // Don't allow editing initial cells
    if (initialBoard[row][col] !== 0) return;
    
    // If there are error cells, don't allow new input until errors are cleared
    if (errorCells.length > 0) {
      setHintMessage('❌ Please fix the incorrect numbers first! They are marked in red.');
      return;
    }
    
    const currentValue = board[row][col];
    if (currentValue === num) return;
    
    // Save state for undo
    const newHistory = [...history, {
      board: board.map(r => [...r]),
      mistakes,
      errorCells
    }];
    setHistory(newHistory);
    
    // Update board
    const newBoard = board.map(r => [...r]);
    newBoard[row][col] = num;
    setBoard(newBoard);
    
    // Check against solution - if wrong, mark as error
    if (num !== solution[row][col]) {
      setErrorCells([{ row, col }]);
      setHintMessage('❌ Incorrect! This number doesn\'t fit. Clear it and try again!');
    } else {
      // Number is correct
      setErrorCells([]);
      setHintMessage('✅ Good job! That\'s the right number!');
    }
    
    // Check if correct
    const response = await fetch(`${API_URL}/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ board: newBoard, row, col, value: num, solution })
    });
    
    const data = await response.json();
    
    if (!data.isCorrect && currentValue === 0) {
      setMistakes(m => m + 1);
    }
    
    // Check if game is won
    const solvedResponse = await fetch(`${API_URL}/check-solved`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ board: newBoard, solution })
    });
    
    const solvedData = await solvedResponse.json();
    if (solvedData.isSolved) {
      setGameWon(true);
      setHintMessage('🎉 Congratulations! You solved the puzzle! 🎉');
    }
  };

  // Handle undo/rollback
  const handleUndo = () => {
    if (history.length === 0 || gameWon) return;
    
    const previousState = history[history.length - 1];
    setBoard(previousState.board);
    setMistakes(previousState.mistakes);
    setErrorCells(previousState.errorCells || []);
    setHistory(history.slice(0, -1));
    setHintMessage('Moved back! Keep going! 💪');
  };

  // Handle hint
  const handleHint = async () => {
    if (gameWon) return;
    
    // Find an empty cell or use selected cell
    let targetRow, targetCol;
    
    if (selectedCell && board[selectedCell.row][selectedCell.col] === 0) {
      targetRow = selectedCell.row;
      targetCol = selectedCell.col;
    } else {
      // Find first empty cell
      for (let r = 0; r < 6; r++) {
        for (let c = 0; c < 6; c++) {
          if (board[r][c] === 0) {
            targetRow = r;
            targetCol = c;
            break;
          }
        }
        if (targetRow !== undefined) break;
      }
    }
    
    if (targetRow === undefined) return;
    
    const response = await fetch(`${API_URL}/hint`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ board, row: targetRow, col: targetCol, solution })
    });
    
    const data = await response.json();
    if (data.success && data.hint) {
      setHintMessage(data.hint.message);
      setHintCell({ row: targetRow, col: targetCol });
      
      // Optionally auto-fill the hint
      const newBoard = board.map(r => [...r]);
      newBoard[targetRow][targetCol] = data.hint.value;
      setBoard(newBoard);
      setSelectedCell({ row: targetRow, col: targetCol });
    }
  };

  // Clear cell
  const handleClearCell = () => {
    if (!selectedCell || gameWon) return;
    
    const { row, col } = selectedCell;
    
    // Don't allow clearing initial cells
    if (initialBoard[row][col] !== 0) return;
    
    const currentValue = board[row][col];
    if (currentValue === 0) return;
    
    // Save state for undo
    const newHistory = [...history, {
      board: board.map(r => [...r]),
      mistakes,
      errorCells
    }];
    setHistory(newHistory);
    
    // Clear the cell
    const newBoard = board.map(r => [...r]);
    newBoard[row][col] = 0;
    setBoard(newBoard);
    
    // Remove this cell from errorCells if it was there
    const newErrorCells = errorCells.filter(e => !(e.row === row && e.col === col));
    setErrorCells(newErrorCells);
    
    if (errorCells.length === 1 && errorCells[0].row === row && errorCells[0].col === col) {
      setHintMessage('✅ Error cleared! You can continue now!');
    } else if (newErrorCells.length > 0) {
      setHintMessage('Cell cleared! Still have incorrect numbers to fix. ❌');
    } else {
      setHintMessage('Cell cleared! You can try again!');
    }
  };

  // Handle keyboard input
  React.useEffect(() => {
    const handleKeyDown = async (e) => {
      // Prevent default for arrow keys to avoid scrolling
      if (e.key.startsWith('Arrow')) {
        e.preventDefault();
      }

      // Handle arrow key navigation (allow moving to clear error cells)
      if (e.key.startsWith('Arrow')) {
        if (!selectedCell) return;
        
        const { row, col } = selectedCell;
        let newRow = row;
        let newCol = col;

        switch (e.key) {
          case 'ArrowUp':
            newRow = Math.max(0, row - 1);
            break;
          case 'ArrowDown':
            newRow = Math.min(5, row + 1);
            break;
          case 'ArrowLeft':
            newCol = Math.max(0, col - 1);
            break;
          case 'ArrowRight':
            newCol = Math.min(5, col + 1);
            break;
          default:
            return;
        }

        setSelectedCell({ row: newRow, col: newCol });
        return;
      }

      // Handle number input (1-6) - blocked if there are error cells
      const num = parseInt(e.key, 10);
      if (!isNaN(num) && num >= 1 && num <= 6) {
        if (errorCells.length > 0) {
          setHintMessage('❌ Please fix the incorrect numbers first! They are marked in red.');
          return;
        }
        await handleNumberInput(num);
        return;
      }

      // Handle backspace/delete to clear cell (allow clearing even with errors)
      if (e.key === 'Backspace' || e.key === 'Delete') {
        handleClearCell();
        return;
      }

      // Handle undo with Ctrl+Z
      if (e.key === 'z' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleUndo();
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedCell, board, initialBoard, solution, history, mistakes, gameWon, errorCells]);

  // Render the board
  const renderBoard = () => {
    return (
      <div className="sudoku-board">
        {board.map((row, rowIndex) => (
          <div key={rowIndex} className="sudoku-row">
            {row.map((cell, colIndex) => {
              const isInitial = initialBoard[rowIndex][colIndex] !== 0;
              const isSelected = selectedCell?.row === rowIndex && selectedCell?.col === colIndex;
              const isHint = hintCell?.row === rowIndex && hintCell?.col === colIndex;
              const isError = errorCells.some(e => e.row === rowIndex && e.col === colIndex) && cell !== 0;
              const isEditable = !isInitial && !gameWon && errorCells.length === 0;
              
              // Calculate box border
              const showRightBorder = (colIndex + 1) % 3 === 0 && colIndex < 5;
              const showBottomBorder = (rowIndex + 1) % 2 === 0 && rowIndex < 5;
              
              return (
                <div
                  key={colIndex}
                  className={`
                    sudoku-cell
                    ${isSelected ? 'selected' : ''}
                    ${isInitial ? 'initial' : ''}
                    ${isHint ? 'hint' : ''}
                    ${isError ? 'error' : ''}
                    ${showRightBorder ? 'border-right' : ''}
                    ${showBottomBorder ? 'border-bottom' : ''}
                  `}
                  onClick={() => handleCellClick(rowIndex, colIndex)}
                >
                  {isSelected && !isInitial && !gameWon ? (
                    <input
                      key={`${rowIndex}-${colIndex}`}
                      type="tel"
                      inputMode="numeric"
                      pattern="[1-6]*"
                      maxLength={1}
                      className={`mobile-input ${isError ? 'error-value' : ''}`}
                      value={cell !== 0 ? cell : ''}
                      onChange={handleMobileInput}
                      onKeyPress={handleMobileKeyPress}
                      onFocus={(e) => e.target.select()}
                      onBlur={() => {
                        // Clear invalid input on blur
                        const currentVal = board[rowIndex][colIndex];
                        if (currentVal !== 0 && (currentVal < 1 || currentVal > 6)) {
                          handleClearCell();
                        }
                      }}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        background: 'transparent',
                        textAlign: 'center',
                        fontSize: '2rem',
                        fontWeight: '700',
                        outline: 'none',
                        caretColor: isError ? '#cc0000' : '#667eea'
                      }}
                    />
                  ) : (
                    <span className={`cell-value ${isInitial ? 'initial-value' : ''} ${isHint ? 'hint-value' : ''} ${isError ? 'error-value' : ''}`}>
                      {cell !== 0 ? cell : ''}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="app">
      <div className="header">
        <h1>🧩 Kids Sudoku</h1>
        <p className="subtitle">6x6 Fun Puzzle Game!</p>
        
        {/* Mobile Menu Dropdown */}
        <div className="mobile-menu">
          <button 
            className="menu-toggle" 
            onClick={() => setShowMenu(!showMenu)}
          >
            ☰
          </button>
          {showMenu && (
            <div className="menu-dropdown">
              <button 
                className="menu-item"
                onClick={() => {
                  handleUndo();
                  setShowMenu(false);
                }}
                disabled={history.length === 0 || gameWon}
                title="Undo last move"
              >
                ↩️ Undo
              </button>
              <button 
                className="menu-item"
                onClick={() => {
                  handleClearCell();
                  setShowMenu(false);
                }}
                disabled={!selectedCell || gameWon}
                title="Clear selected cell"
              >
                🗑️ Clear
              </button>
              <button 
                className="menu-item"
                onClick={() => {
                  handleReset();
                  setShowMenu(false);
                }}
                disabled={gameWon}
                title="Reset current puzzle"
              >
                🔄 Reset
              </button>
              <button 
                className="menu-item"
                onClick={() => {
                  generatePuzzle();
                  setShowMenu(false);
                }}
                title="Start a new game"
              >
                🎮 New Game
              </button>
            </div>
          )}
        </div>
      </div>

      {gameWon && (
        <div className="win-message">
          <h2>🎉🎊 You Did It! 🎊🎉</h2>
          <p>Amazing job! You're a Sudoku Super Star! ⭐</p>
          <button onClick={() => generatePuzzle()}>New Game</button>
        </div>
      )}

      {hintMessage && !gameWon && (
        <div className={`hint-message ${hintMessage.includes('Congratulations') ? 'win' : ''}`}>
          <p>{hintMessage}</p>
        </div>
      )}

      <div className="game-container">
        <div className="sidebar">
          <div className="stats-panel">
            <div className="stat-item">
              <span className="stat-label">Mistakes:</span>
              <span className="stat-value">{mistakes}/3</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Difficulty:</span>
              <span className="stat-value">{difficulty.toUpperCase()}</span>
            </div>
          </div>

          <div className="controls-panel">
            <h3>Controls</h3>
            <button 
              className="control-btn undo-btn" 
              onClick={handleUndo}
              disabled={history.length === 0 || gameWon}
              title="Go back one step"
            >
              ↩️ Undo
            </button>
            <button 
              className="control-btn hint-btn" 
              onClick={handleHint}
              disabled={gameWon}
              title="Get a helpful hint"
            >
              💡 Hint
            </button>
            <button 
              className="control-btn clear-btn" 
              onClick={handleClearCell}
              disabled={!selectedCell || gameWon}
              title="Clear selected cell"
            >
              🗑️ Clear
            </button>
            <button 
              className="control-btn new-game-btn" 
              onClick={() => generatePuzzle()}
            >
              🔄 New Game
            </button>
          </div>

          <div className="difficulty-panel">
            <h3>Difficulty</h3>
            <div className="difficulty-buttons">
              <button 
                className={`difficulty-btn ${difficulty === 'easy' ? 'active' : ''}`}
                onClick={() => generatePuzzle('easy')}
              >
                🌟 Easy
              </button>
              <button 
                className={`difficulty-btn ${difficulty === 'medium' ? 'active' : ''}`}
                onClick={() => generatePuzzle('medium')}
              >
                🌟🌟 Medium
              </button>
              <button 
                className={`difficulty-btn ${difficulty === 'hard' ? 'active' : ''}`}
                onClick={() => generatePuzzle('hard')}
              >
                🌟🌟🌟 Hard
              </button>
            </div>
          </div>
        </div>

        <div className="board-container">
          {renderBoard()}
        </div>

        <div className="numpad">
          <h3>Numbers</h3>
          <div className="numpad-grid">
            {[1, 2, 3, 4, 5, 6].map(num => (
              <button
                key={num}
                className="num-btn"
                onClick={() => handleNumberInput(num)}
                disabled={gameWon || (selectedCell && initialBoard[selectedCell.row]?.[selectedCell.col] !== 0) || errorCells.length > 0}
              >
                {num}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="instructions">
        <h3>📚 How to Play</h3>
        <ul>
          <li>Fill each row with numbers 1-6</li>
          <li>Fill each column with numbers 1-6</li>
          <li>Fill each 2×3 box with numbers 1-6</li>
          <li>Use <strong>Arrow Keys</strong> to move between cells</li>
          <li>Use <strong>Numbers 1-6</strong> on your keyboard to input numbers</li>
          <li>Use <strong>Backspace/Delete</strong> to clear a cell</li>
          <li>Use <strong>Ctrl+Z (or Cmd+Z)</strong> to undo</li>
          <li>Use <strong>Undo</strong> button to go back if you make a mistake</li>
          <li>Use <strong>Hint</strong> if you need help! 💡</li>
          <li><strong>Incorrect numbers will turn red and block further input!</strong></li>
        </ul>
      </div>
    </div>
  );
}

export default App;